import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { signOut } from '../core/api/auth-service';
import { useLanguage } from '../core/hooks/use-language';
import { useSession } from '../core/hooks/use-session';
import { useTranslation } from '../core/hooks/use-translation';
import { LANGUAGES } from '../core/i18n/languages';
import { switchLanguagePath } from '../core/i18n/switch-language-path';
import type { Language } from '../core/i18n/language';
import type { TranslationKey } from '../core/i18n/translation';
import { Avatar } from '../ui/avatar';
import { rememberLanguage } from './language-memory';
import { useLanguageAlternates } from './language-alternates';
import { NotificationBell } from './notification-bell';
import { ThemeSwitch } from './theme-switch';

/** The marketing anchors the live site's navigation carries, in its order. */
const SECTIONS: readonly { hash: string; label: TranslationKey }[] = [
    { hash: '#about', label: 'site.nav.about' },
    { hash: '#mortgage-programs', label: 'site.nav.programs' },
    { hash: '#faq', label: 'site.nav.faq' },
    { hash: '#contact', label: 'site.nav.contact' }
];

/**
 * The bar above every page that is not the landing page, the wizard or the legal document.
 *
 * **It is sticky and it is one row, at every width.** What was here before wrapped: eight links, a
 * button, a sign-out and a language pair, all at the same weight in one flex row that folded onto
 * three lines below about 900px and pushed the page content off the first screen. The marketing
 * anchors collapse into a menu below `xl`, and nothing in the bar is allowed to break across lines —
 * a wrapped two-word link doubles the height of a sticky header rather than saving space.
 *
 * `xl` and not `lg` because the signed-in row is the wide one: an avatar, a name and a sign-out
 * appear beside everything the signed-out row already carries, and `lg` left them overlapping the
 * anchors on a laptop.
 *
 * **The appearance switch lives here rather than on the account screens alone.** It used to sit
 * beside the sign-in heading because those were the only screens with a dark pass; the portal, the
 * blog and an article have one now, and every page that renders this header is a page that can
 * answer the button.
 */
export function SiteHeader() {
    const { t } = useTranslation();
    const language = useLanguage();
    const session = useSession();
    const location = useLocation();

    const [menuOpen, setMenuOpen] = useState(false);

    // A menu that survives the navigation it triggered is a menu covering the page somebody just
    // asked for. The path is the dependency, not the click, so a link to the page already open
    // still closes it.
    useEffect(() => setMenuOpen(false), [location.pathname, location.hash]);

    const path = `${location.pathname}${location.search}${location.hash}`;

    return (
        <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/85 backdrop-blur-md transition-colors dark:border-slate-800 dark:bg-slate-950/85">
            <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
                <Link
                    to={`/${language}`}
                    className="flex shrink-0 items-center gap-2 text-base font-extrabold whitespace-nowrap text-slate-900 dark:text-slate-100"
                >
                    <span className="flex size-8 items-center justify-center rounded-lg bg-brand-600">
                        <img src="/favicon.ico" alt="" className="size-6" />
                    </span>
                    <span className="hidden sm:inline">{t('brand.name')}</span>
                </Link>

                <nav className="ms-auto hidden items-center gap-6 text-sm xl:flex">
                    {SECTIONS.map((section) => (
                        <Link
                            key={section.hash}
                            to={`/${language}${section.hash}`}
                            className="font-semibold whitespace-nowrap text-slate-600 transition hover:text-brand-700 dark:text-slate-400 dark:hover:text-brand-300"
                        >
                            {t(section.label)}
                        </Link>
                    ))}

                    <Link
                        to={`/${language}/blog`}
                        className="font-semibold whitespace-nowrap text-slate-600 transition hover:text-brand-700 dark:text-slate-400 dark:hover:text-brand-300"
                    >
                        {t('nav.blog')}
                    </Link>
                </nav>

                <div className="ms-auto flex items-center gap-2 xl:ms-0">
                    <LanguageSwitcher current={language} path={path} />
                    <ThemeSwitch />

                    <Link
                        to={`/${language}/book`}
                        className="hidden rounded-xl bg-brand-600 px-4 py-2 text-sm font-bold whitespace-nowrap text-white transition hover:bg-brand-700 sm:inline-flex"
                    >
                        {t('site.nav.cta')}
                    </Link>

                    {session === null ? (
                        <Link
                            to={`/${language}/account/sign-in`}
                            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold whitespace-nowrap text-slate-700 transition hover:border-brand-400 hover:text-brand-700 dark:border-slate-700 dark:text-slate-200 dark:hover:border-brand-600 dark:hover:text-brand-300"
                        >
                            {t('nav.sign_in')}
                        </Link>
                    ) : (
                        <div className="flex items-center gap-1">
                            <NotificationBell />

                            <Link
                                to={`/${language}/portal`}
                                title={t('nav.portal')}
                                aria-label={t('nav.portal')}
                                className="flex items-center gap-2 rounded-xl px-2 py-1.5 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                                <Avatar name={session.user.displayName} className="size-8 text-xs" />
                                {/* Hidden from xl to 2xl: the full nav appears at xl, and with the label the row is wider than a 1280–1400px screen. */}
                                <span className="hidden text-sm font-semibold whitespace-nowrap text-slate-700 md:inline xl:hidden 2xl:inline dark:text-slate-200">
                                    {t('nav.portal')}
                                </span>
                            </Link>

                            <button
                                type="button"
                                onClick={() => void signOut()}
                                className="hidden rounded-xl px-2.5 py-2 text-sm font-medium whitespace-nowrap text-slate-500 transition hover:text-slate-900 sm:inline-flex dark:text-slate-400 dark:hover:text-slate-200"
                            >
                                {t('nav.sign_out')}
                            </button>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setMenuOpen((open) => !open)}
                        aria-expanded={menuOpen}
                        aria-controls="site-menu"
                        aria-label={t(menuOpen ? 'nav.close_menu' : 'nav.open_menu')}
                        className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-slate-300 text-slate-600 xl:hidden dark:border-slate-700 dark:text-slate-300"
                    >
                        <svg
                            className="size-5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            aria-hidden="true"
                        >
                            {menuOpen ? <path d="m6 6 12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
                        </svg>
                    </button>
                </div>
            </div>

            {/*
                Rendered rather than hidden: a collapsed menu that stays in the document keeps its
                links in the tab order behind the page, which is how a keyboard ends up somewhere
                invisible. The button owns it through `aria-controls`.
            */}
            {menuOpen && (
                <nav
                    id="site-menu"
                    className="border-t border-slate-200 bg-white px-4 py-3 xl:hidden dark:border-slate-800 dark:bg-slate-950"
                >
                    <ul className="space-y-1">
                        {SECTIONS.map((section) => (
                            <li key={section.hash}>
                                <Link
                                    to={`/${language}${section.hash}`}
                                    className="block rounded-lg px-3 py-2.5 font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                                >
                                    {t(section.label)}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to={`/${language}/blog`}
                                className="block rounded-lg px-3 py-2.5 font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                            >
                                {t('nav.blog')}
                            </Link>
                        </li>
                        <li className="pt-1 sm:hidden">
                            <Link
                                to={`/${language}/book`}
                                className="block rounded-xl bg-brand-600 px-3 py-2.5 text-center font-bold text-white"
                            >
                                {t('site.nav.cta')}
                            </Link>
                        </li>
                        {/* Below `sm` the signed-in row has no room for it beside the menu button. */}
                        {session !== null && (
                            <li className="border-t border-slate-200 pt-1 sm:hidden dark:border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => void signOut()}
                                    className="block w-full rounded-lg px-3 py-2.5 text-start font-semibold text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                                >
                                    {t('nav.sign_out')}
                                </button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}

/**
 * Swaps the language segment of the current URL rather than sending the visitor home, so a
 * switch keeps them on the page they were reading.
 *
 * The query string travels with it. It was dropped once, and the page that suffered is the
 * activation screen: `/kk/account/activate?email=…&token=…` became `/ru/account/activate` with
 * nothing on it, so a customer who switched language was told their invitation link was
 * incomplete — and re-issuing it is the only recovery path this product has.
 *
 * **Rewriting the prefix is right for every page whose path is the same in both languages, and
 * wrong for the one where it is not.** An article's slug is per language, so a page that knows its
 * own counterpart publishes it through `usePublishLanguageAlternates` and this uses that instead.
 *
 * The rewrite itself is `switchLanguagePath`, extracted so it can be tested: it is three lines of
 * string handling that has already been wrong once, on a page where being wrong costs a customer
 * their invitation.
 */
function LanguageSwitcher({ current, path }: { current: Language; path: string }) {
    const alternates = useLanguageAlternates();

    return (
        <span className="flex items-center rounded-xl bg-slate-100 p-0.5 dark:bg-slate-800">
            {LANGUAGES.map((language) => (
                <Link
                    key={language}
                    to={alternates?.[language] ?? switchLanguagePath(path, current, language)}
                    onClick={() => rememberLanguage(language)}
                    aria-current={language === current ? 'true' : undefined}
                    className={`rounded-lg px-2.5 py-1.5 text-xs font-bold uppercase transition ${
                        language === current
                            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-slate-100'
                            : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                >
                    {language}
                </Link>
            ))}
        </span>
    );
}
